// Axios configuration for Rails integration
import axios from 'axios'
import { useNotificationStore } from '../stores/notifications'

let notificationStore = null
function getNotifications() {
  if (!notificationStore) {
    try { notificationStore = useNotificationStore() } catch { /* pinia not ready */ }
  }
  return notificationStore
}

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: '/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})

// Get CSRF token from Rails meta tag
function getCSRFToken() {
  const token = document.querySelector('meta[name="csrf-token"]')
  return token ? token.getAttribute('content') : null
}

// Devise rotates the session CSRF token on sign-in and sign-out, which makes the token
// rendered in the initial page load stale. Auth endpoints return the fresh token and the
// auth store pushes it back here, so subsequent requests keep passing forgery protection.
function setCSRFToken(token) {
  if (!token) return
  let meta = document.querySelector('meta[name="csrf-token"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute('name', 'csrf-token')
    document.head.appendChild(meta)
  }
  meta.setAttribute('content', token)
}

// Request interceptor - add CSRF token to all requests
apiClient.interceptors.request.use(
  (config) => {
    // Add CSRF token for Rails
    const csrfToken = getCSRFToken()
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken
    }

    // Authentication is cookie-based (Devise session); no bearer token involved.

    if (import.meta.env.DEV) {
      console.log(`🚀 ${config.method?.toUpperCase()} ${config.url}`, config.data || config.params)
    }
    return config
  },
  (error) => {
    if (import.meta.env.DEV) console.error('❌ Request error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor - handle common responses
apiClient.interceptors.response.use(
  (response) => {
    if (import.meta.env.DEV) {
      console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url}`, response.data)
    }
    return response
  },
  (error) => {
    if (import.meta.env.DEV) console.error('❌ Response error:', error.response?.data || error.message)

    const notifications = getNotifications()

    if (error.response) {
      const msg = error.response.data?.error || error.response.data?.message
      switch (error.response.status) {
        case 401:
          // Let Vue router guard handle redirect
          break
        case 403:
          notifications?.error(msg || 'Access forbidden')
          break
        case 422:
          if (msg === 'invalid_authenticity_token') {
            // Session token went stale (server restart, expired session). A reload
            // pulls a fresh CSRF token from the Rails layout.
            notifications?.warning('Your session expired. Reloading…')
            window.setTimeout(() => window.location.reload(), 1500)
          } else {
            notifications?.warning(msg || 'Validation error')
          }
          break
        case 500:
          notifications?.error('Server error. Please try again.')
          break
      }
    } else if (error.request) {
      notifications?.error('Network error. Check your connection.')
    }
    
    return Promise.reject(error)
  }
)

// Create a simple client for non-API requests (Rails forms, etc.)
const railsClient = axios.create({
  timeout: 10000,
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded',
    'Accept': 'text/html,application/xhtml+xml'
  }
})

// Add CSRF token to Rails client too
railsClient.interceptors.request.use((config) => {
  const csrfToken = getCSRFToken()
  if (csrfToken) {
    config.headers['X-CSRF-Token'] = csrfToken
  }
  return config
})

export { apiClient, railsClient, setCSRFToken }
export default apiClient
